import React from "react";
import { Tooltip } from "antd";
import { useDashTheme } from "../../hooks/useDashTheme";
import { SectionCard } from "../primitives";
import { InfoDot } from "../classic/InfoDot";
import { Shim } from "../classic/micro";
import { useRevealRoot, anim, animStyle, drawRef, CountUp } from "./motion";
import {
  H, pctOf, buildHealth, buildVerdict, buildMovement, squarify, projectTiles,
  buildQuadrant, buildDayShape, activeHourWindow, buildHeat, ZONES, WEEKDAYS,
} from "./compose";

/**
 * Pulse panels — the redesigned overview.
 *
 * House rules every panel here follows:
 *   · it states its conclusion in words and the graphic proves it;
 *   · a series colour means WHICH entity, never how big or how good — size is
 *     length/area and direction is which side of zero, so hue stays free;
 *   · every series is direct-labeled, which is the documented relief for the
 *     light-theme contrast warning on tokens.series.light;
 *   · nothing is claimed that the payload does not carry.
 */

const fmtH = (m) => `${H(m)}h`;
const hm = (m) => `${Math.floor((m || 0) / 60)}h ${Math.round((m || 0) % 60)}m`;
const inkOn = (hex) => {
  const s = String(hex).replace("#", "");
  if (s.length !== 6) return "#FFFFFF";
  const lin = (c) => { const x = parseInt(c, 16) / 255; return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(s.slice(0, 2)) + 0.7152 * lin(s.slice(2, 4)) + 0.0722 * lin(s.slice(4, 6));
  return L > 0.21 ? "#0B1210" : "#FFFFFF";
};

// Fixed tile layout for the ProjectsCard treemap skeleton — one big tile +
// three smaller ones, the shape a real squarify() call tends to produce.
const TREEMAP_SKELETON = [
  { x: 0, y: 0, w: 0.56, h: 1 },
  { x: 0.56, y: 0, w: 0.44, h: 0.52 },
  { x: 0.56, y: 0.52, w: 0.22, h: 0.48 },
  { x: 0.78, y: 0.52, w: 0.22, h: 0.48 },
];
// Bar heights (fraction of track) for the RhythmCard skeleton — an organic
// repeating pattern so columns read as bars, not a uniform block.
const RHYTHM_BAR_SKELETON = [0.5, 0.78, 0.38, 0.64, 0.86, 0.46, 0.32];
// Fixed scatter positions (% of plot area) for the PeopleQuadrant skeleton.
const QUADRANT_DOT_SKELETON = [
  { x: 22, y: 68 }, { x: 34, y: 40 }, { x: 41, y: 58 }, { x: 52, y: 30 },
  { x: 58, y: 52 }, { x: 63, y: 22 }, { x: 70, y: 45 }, { x: 76, y: 65 },
  { x: 30, y: 78 }, { x: 85, y: 35 }, { x: 47, y: 72 }, { x: 66, y: 60 },
];
// Fixed height/opacity pattern (12 columns, core-hours-shaped bell curve) for
// the WeekHeatmap skeleton's hour strip + grid cells.
const HEATMAP_STRIP_SKELETON = [0.25, 0.4, 0.6, 0.8, 0.95, 0.7, 0.85, 1, 0.75, 0.5, 0.35, 0.2];

/* ═══════════════════ 1. the verdict band ═══════════════════════════════ */
export function HealthBand({ overview, goals, loading, onTeam }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const health = React.useMemo(() => buildHealth(overview, goals), [overview, goals]);
  const verdict = React.useMemo(() => buildVerdict(overview), [overview]);
  const tot = overview?.totals;

  if (loading) {
    // Same 3-column shape as the real band — gauge ring, verdict lines +
    // chip pills, pillar bars — not one flat rectangle.
    return (
      <div style={{
        background: t.c.surface, borderRadius: 16, boxShadow: t.shadow.card, padding: `${t.s[6]}px ${t.s[6]}px`,
        display: "grid", gap: t.s[6], alignItems: "center",
        gridTemplateColumns: "minmax(200px, 216px) minmax(260px, 1.2fr) minmax(220px, .9fr)",
      }} className="dashv2-pulse-verdict">
        <div style={{ justifySelf: "center" }}><Shim w={176} h={176} r={999} /></div>
        <div>
          <Shim w="86%" h={20} />
          <Shim w="46%" h={20} style={{ marginTop: 9 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {[64, 96, 78, 112].map((w, i) => <Shim key={i} w={w} h={22} r={999} />)}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[3] }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "78px 1fr 28px", gap: t.s[3], alignItems: "center" }}>
              <Shim w={56} h={11} />
              <Shim h={7} r={999} />
              <Shim w={22} h={11} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  /* No score at all, or a score of zero — both mean nothing was tracked, since
     every pillar needs tracked time to be non-zero. Hiding the band is better
     than leading the page with an alarming 0/100 for an org with no data. */
  if (!health.score) return null;

  // 240° arc: 0 at the lower left, 100 at the lower right
  const A0 = -120, A1 = 120, R = 80, SW = 14, CX = 108, CY = 110;
  const rad = (d) => ((d - 90) * Math.PI) / 180;
  const pt = (d, r) => [CX + Math.cos(rad(d)) * r, CY + Math.sin(rad(d)) * r];
  const arcD = (a, b, r) => {
    const p0 = pt(a, r); const p1 = pt(b, r);
    return `M${p0[0].toFixed(1)} ${p0[1].toFixed(1)}A${r} ${r} 0 ${Math.abs(b - a) > 180 ? 1 : 0} 1 ${p1[0].toFixed(1)} ${p1[1].toFixed(1)}`;
  };
  const at = (v) => A0 + (Math.max(0, Math.min(100, v)) / 100) * (A1 - A0);

  return (
    <div
      ref={ref}
      style={{
        background: `radial-gradient(120% 150% at 6% 50%, ${t.c.primarySoft}, transparent 58%), ${t.c.surface}`,
        borderRadius: 16, boxShadow: t.shadow.card, padding: `${t.s[6]}px ${t.s[6]}px`,
        display: "grid", gap: t.s[6], alignItems: "center",
        gridTemplateColumns: "minmax(200px, 216px) minmax(260px, 1.2fr) minmax(220px, .9fr)",
      }}
      className="dashv2-pulse-verdict"
    >
      {/* gauge */}
      <div style={{ position: "relative", width: "100%", maxWidth: 216, justifySelf: "center" }}>
        <svg viewBox="0 0 216 186" style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
          role="img" aria-label={`Organisation health ${health.score} out of 100`}>
          <path d={arcD(A0, A1, R)} fill="none" stroke={t.c.surfaceMuted} strokeWidth={SW} strokeLinecap="round"
            {...anim("fade", 0.06)} />
          <path d={arcD(A0, at(health.score), R)} fill="none" stroke={t.c.primary} strokeWidth={SW} strokeLinecap="round"
            className="tgp-draw" ref={drawRef(0.14)} />
          {health.pillars.map((p, i) => {
            const pos = pt(at(p.value), R);
            return (
              <Tooltip key={p.key} title={`${p.key} · ${p.value} — ${p.why}`} mouseEnterDelay={0.05}>
                <circle cx={pos[0]} cy={pos[1]} r={6} fill={t.series[i]} stroke={t.c.surface} strokeWidth={2.5}
                  style={{ cursor: "help", ...animStyle(0.58 + i * 0.07) }} className="tgp-pop" />
              </Tooltip>
            );
          })}
          {[["0", A0], ["100", A1]].map(([label, a]) => {
            const p = pt(a, R + 19);
            return <text key={label} x={p[0]} y={p[1] + 4} textAnchor="middle" fill={t.c.textTertiary}
              fontSize={10} {...anim("fade", 0.12)}>{label}</text>;
          })}
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", padding: "14px 34px 0", pointerEvents: "none" }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: t.c.textTertiary }}>
            Org health
          </span>
          <span style={{ fontSize: 52, fontWeight: 600, letterSpacing: "-.04em", lineHeight: 1, color: t.c.text }}>
            <CountUp value={health.score} format={(x) => String(Math.round(x))} />
          </span>
          <span style={{ fontSize: 10.5, color: t.c.textTertiary, letterSpacing: ".06em", marginTop: 2 }}>OUT OF 100</span>
        </div>
      </div>

      {/* verdict copy */}
      <div {...anim("rise", 0.18)}>
        <h3 style={{ margin: 0, fontSize: "clamp(18px, 2vw, 23px)", lineHeight: 1.3, fontWeight: 600,
          letterSpacing: "-.022em", color: t.c.text, textWrap: "balance" }}>
          {verdict ? verdict.lead : "Nothing tracked in this window"}
          {verdict?.tail ? <> — <span style={{ color: t.c.primary }}>{verdict.tail}</span></> : "."}
        </h3>
        <div style={{ display: "flex", gap: t.s[2], flexWrap: "wrap", marginTop: t.s[4] }}>
          {tot ? <Chip t={t}>{H(tot.trackedMinutes).toLocaleString()}h tracked</Chip> : null}
          {verdict?.delta != null ? (
            <Chip t={t} tone={verdict.delta >= 0 ? "up" : "down"}>
              {verdict.delta >= 0 ? "▲" : "▼"} {Math.abs(verdict.delta)}% vs last period
            </Chip>
          ) : null}
          {tot ? <Chip t={t}>{pctOf(tot.activeMinutes, tot.trackedMinutes)}% active</Chip> : null}
          {overview?.people?.silent?.length ? (
            <Chip t={t} tone="down" onClick={onTeam}>
              {overview.people.silent.length} of {overview.people.total} not tracking
            </Chip>
          ) : null}
        </div>
      </div>

      {/* pillars */}
      <div style={{ display: "flex", flexDirection: "column", gap: t.s[3], minWidth: 0 }}>
        {/* The composite is the loudest number on the page, so it carries the
            ⓘ that explains which four bands it averages. */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -4 }}>
          <InfoDot metric="orgHealth" />
        </div>
        {health.pillars.map((p, i) => (
          <Tooltip key={p.key} title={p.why} mouseEnterDelay={0.05}>
            <div style={{ display: "grid", gridTemplateColumns: "78px 1fr 28px", gap: t.s[3],
              alignItems: "center", cursor: "help", ...animStyle(0.22 + i * 0.07) }} className="tgp-rise">
              <span style={{ fontSize: 11.5, color: t.c.textSecondary, fontWeight: 500 }}>{p.key}</span>
              <span style={{ height: 7, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden",
                boxShadow: `inset 0 0 0 1px ${t.c.border}` }}>
                <i style={{ display: "block", height: "100%", width: `${p.value}%`, borderRadius: 999,
                  background: t.series[i], ...animStyle(0.28 + i * 0.07) }} className="tgp-gx" />
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: "right", color: t.c.text,
                fontVariantNumeric: "tabular-nums" }}>{p.value}</span>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

function Chip({ t, tone, children, onClick }) {
  const color = tone === "up" ? t.status.positive : tone === "down" ? t.status.negative : t.c.textSecondary;
  return (
    <span
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
        padding: "3.5px 9px", borderRadius: 999, background: t.c.surfaceMuted, color,
        border: `1px solid ${t.c.border}`, whiteSpace: "nowrap", cursor: onClick ? "pointer" : "default",
      }}
      {...anim("rise", 0.3)}
    >
      {children}
    </span>
  );
}

/* ═══════════════════ 2. projects: treemap + movement ═══════════════════ */
export function ProjectsCard({ overview, loading, onDrill, onViewAll }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const tiles = React.useMemo(() => projectTiles(overview, 8), [overview]);
  const slots = React.useMemo(() => squarify(tiles.map((x) => x.minutes), 1, 1), [tiles]);
  const move = React.useMemo(() => buildMovement(overview), [overview]);

  const twoShare = tiles.slice(0, 2).reduce((a, x) => a + x.share, 0);

  const answer = !tiles.length ? "No project time in this window."
    : move.tookIt
      ? `The company gained ${move.orgGain}h. ${move.tookIt.name} took ${move.tookIt.gain}h of it on its own — it grew by taking, not from new capacity.`
      : `The top ${Math.min(2, tiles.length)} hold ${twoShare}% of tracked time. Area is share; the bars beside it are hours gained or lost.`;

  const maxUp = Math.max(0, ...move.rows.map((r) => Math.max(0, r.gain)));
  const maxDn = Math.max(0, ...move.rows.map((r) => Math.max(0, -r.gain)));
  const span = maxUp + maxDn;
  const zero = span > 0 ? (maxDn / span) * 100 : 0;

  return (
    <SectionCard
      title="Where the organisation spent itself"
      subtitle={answer}
      padding={16}
      actions={<>{onViewAll ? <button type="button" onClick={onViewAll} style={ghost(t)}>All projects</button> : null}<InfoDot metric="spend" /></>}
    >
      <div ref={ref} className="dashv2-pulse-spent"
        style={{ display: "grid", gridTemplateColumns: "minmax(0,1.15fr) minmax(0,1fr)", gap: t.s[7], alignItems: "start" }}>
        {/* treemap */}
        <div>
          <Micro t={t}>Share of tracked time — area is the measure</Micro>
          {loading ? (
            // Same squarified-tile shape as the real treemap, not one block.
            <div style={{ position: "relative", width: "100%", aspectRatio: "2 / 1", marginTop: 10 }}>
              {TREEMAP_SKELETON.map((s, i) => (
                <div key={i} style={{
                  position: "absolute", left: `${s.x * 100}%`, top: `${s.y * 100}%`,
                  width: `calc(${s.w * 100}% - 5px)`, height: `calc(${s.h * 100}% - 5px)`, margin: 2.5,
                }}>
                  <Shim w="100%" h="100%" r={10} />
                </div>
              ))}
            </div>
          ) : !tiles.length ? (
            <Empty t={t}>No project time recorded in this window.</Empty>
          ) : (
            <div style={{ position: "relative", width: "100%", aspectRatio: "2 / 1", minHeight: 0, marginTop: 10 }}>
              {slots.map((s, n) => {
                const tile = tiles[s.index];
                const col = t.series[tile.slot % t.series.length];
                const ink = inkOn(col);
                const small = s.w < 0.16 || s.h < 0.24;
                return (
                  <Tooltip key={tile.key} mouseEnterDelay={0.05}
                    title={`${tile.name} · ${fmtH(tile.minutes)} (${tile.share}%)${tile.prevMinutes ? ` · was ${fmtH(tile.prevMinutes)}` : ""}`}>
                    <div
                      role={tile.drill ? "button" : undefined}
                      tabIndex={tile.drill ? 0 : undefined}
                      onClick={tile.drill && onDrill ? () => onDrill(tile.drill) : undefined}
                      onKeyDown={tile.drill && onDrill ? (e) => { if (e.key === "Enter") onDrill(tile.drill); } : undefined}
                      className="tgp-pop"
                      style={{
                        position: "absolute", left: `${s.x * 100}%`, top: `${s.y * 100}%`,
                        width: `calc(${s.w * 100}% - 5px)`, height: `calc(${s.h * 100}% - 5px)`, margin: 2.5,
                        background: col, backgroundImage: "linear-gradient(157deg, rgba(255,255,255,.13), rgba(0,0,0,.16))",
                        color: ink, borderRadius: 10, padding: small ? "8px 9px" : "11px 12px", overflow: "hidden",
                        display: "flex", flexDirection: "column", justifyContent: "space-between",
                        cursor: tile.drill && onDrill ? "pointer" : "default",
                        ...animStyle(0.1 + n * 0.055),
                      }}
                    >
                      <span style={{ fontSize: small ? 10.5 : 12.5, fontWeight: 700, letterSpacing: "-.012em", lineHeight: 1.22 }}>
                        {tile.name}
                      </span>
                      <span>
                        <span style={{ display: "block", fontSize: small ? 13 : 18, fontWeight: 700, letterSpacing: "-.028em", lineHeight: 1 }}>
                          {fmtH(tile.minutes)}
                        </span>
                        <span style={{ display: "block", fontSize: small ? 9.5 : 10.5, fontWeight: 600, opacity: 0.8, marginTop: 2 }}>
                          {tile.share}%
                        </span>
                      </span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>

        {/* movement */}
        <div style={{ minWidth: 0 }}>
          <Micro t={t}>What moved, in hours against last period</Micro>
          {loading ? (
            // Same row grid as the real diverging-bar list, not one block.
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
              {[46, 68, 30, 58, 40].map((w, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(64px,108px) minmax(0,1fr) 48px", gap: t.s[3], alignItems: "center" }}>
                  <Shim w={72} h={11} />
                  <Shim h={12} r={4} w={`${w}%`} />
                  <Shim w={34} h={11} />
                </div>
              ))}
            </div>
          ) : !move.rows.length ? (
            <Empty t={t}>Nothing to compare yet.</Empty>
          ) : span === 0 ? (
            <Empty t={t}>Every project held exactly level on the previous period.</Empty>
          ) : (
            <>
              <div style={{ marginTop: 10 }}>
                {move.rows.map((r, i) => {
                  const col = t.series[r.slot % t.series.length];
                  const w = (Math.abs(r.gain) / span) * 100;
                  const pos = r.gain >= 0;
                  return (
                    <Tooltip key={r.projectId || r.name} mouseEnterDelay={0.05}
                      title={`${r.name} · ${fmtH(r.was)} → ${fmtH(r.now)}${r.isNew ? " (new this period)" : ""}`}>
                      <div
                        role={onDrill ? "button" : undefined}
                        tabIndex={onDrill ? 0 : undefined}
                        onClick={onDrill ? () => onDrill({ kind: "project", id: r.projectId, name: r.name }) : undefined}
                        onKeyDown={onDrill ? (e) => { if (e.key === "Enter") onDrill({ kind: "project", id: r.projectId, name: r.name }); } : undefined}
                        className="tgp-rise"
                        style={{
                          display: "grid", gridTemplateColumns: "minmax(64px,108px) minmax(0,1fr) 48px",
                          gap: t.s[3], alignItems: "center", padding: "8px 6px", borderRadius: 8,
                          cursor: onDrill ? "pointer" : "default", ...animStyle(0.14 + i * 0.055),
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: t.c.text }}>
                          <i style={{ width: 8, height: 8, borderRadius: 2, background: col, flex: "0 0 auto" }} />
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                        </span>
                        <span style={{ position: "relative", height: 16 }}>
                          <i style={{ position: "absolute", top: -8, bottom: -8, left: `${zero}%`, width: 1, background: t.c.borderStrong }} />
                          <i className="tgp-gx" style={{
                            position: "absolute", top: 2, height: 12, background: col,
                            left: pos ? `${zero}%` : `${zero - w}%`, width: `${w}%`,
                            borderRadius: pos ? "0 4px 4px 0" : "4px 0 0 4px",
                            ...animStyle(0.22 + i * 0.055, pos ? null : { "--tgp-ox": "right" }),
                          }} />
                        </span>
                        <span style={{ fontSize: 11.5, fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums",
                          color: r.gain === 0 ? t.c.textTertiary : r.gain > 0 ? t.status.positive : t.status.negative }}>
                          {r.gain > 0 ? "+" : ""}{r.gain}h
                        </span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(64px,108px) minmax(0,1fr) 48px",
                gap: t.s[3], padding: "0 6px", marginTop: 6 }} {...anim("fade", 0.54)}>
                <span />
                <span style={{ position: "relative", height: 14, fontSize: 9.5, fontWeight: 600, color: t.c.textTertiary }}>
                  {maxDn > 0 ? <span style={{ position: "absolute", left: 0 }}>−{maxDn}h</span> : null}
                  <span style={{ position: "absolute", left: `${zero}%`, transform: "translateX(-50%)", color: t.c.textSecondary }}>0</span>
                  {maxUp > 0 ? <span style={{ position: "absolute", right: 0 }}>+{maxUp}h</span> : null}
                </span>
                <span />
              </div>
              {move.tookIt ? (
                <p style={{ margin: `${t.s[4]}px 0 0`, fontSize: 11.5, lineHeight: 1.6, color: t.c.textTertiary,
                  borderLeft: `2px solid ${t.c.primaryRing}`, padding: "1px 0 1px 12px" }} {...anim("rise", 0.6)}>
                  The company gained <b style={{ color: t.c.text }}>{move.orgGain}h</b> this period.{" "}
                  <b style={{ color: t.c.text }}>{move.tookIt.name}</b> gained{" "}
                  <b style={{ color: t.c.text }}>{move.tookIt.gain}h</b> on its own, so everything else nets out at{" "}
                  <b style={{ color: t.c.text }}>{move.tookIt.rest}h</b>.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/* ═══════════════════ 3. people quadrant ════════════════════════════════ */
export function PeopleQuadrant({ teamRows, loading, rangeDays, onMember, onViewAll }) {
  const t = useDashTheme();
  const ref = useRevealRoot([teamRows]);
  const q = React.useMemo(() => buildQuadrant(teamRows, undefined, rangeDays), [teamRows, rangeDays]);

  const W = 760, Hh = 380, L = 54, R = 18, T = 16, B = 46;
  const pw = W - L - R, ph = Hh - T - B;
  /* The floor follows the data. A fixed 40% clamped anyone below it onto the
     axis, where they read as exactly 40% — precisely the people the "needs a
     conversation" zone exists to surface. */
  const lowest = q.points.reduce((a, p) => Math.min(a, p.share), 100);
  const yMin = Math.max(0, Math.min(40, Math.floor((lowest - 5) / 10) * 10));
  const X = (h) => L + (Math.min(h, q.xMax) / q.xMax) * pw;
  const Y = (u) => T + ph - ((Math.max(u, yMin) - yMin) / Math.max(1, 100 - yMin)) * ph;
  const xT = X(q.hoursTarget); const yT = Y(q.activeTarget);
  const tone = { positive: t.status.positive, info: t.status.info, warning: t.status.warning, negative: t.status.negative };

  const answer = !q.points.length
    ? "No one tracked time in this window."
    : `Hours across, focus up. ${q.counts.performing} of ${q.points.length} are performing`
      + `${q.counts.stretched ? `, ${q.counts.stretched} stretched thin` : ""}`
      + `${q.counts.check ? `, ${q.counts.check} need a conversation` : ""}.`;

  const zoneBoxes = [
    { key: "performing", x: xT, y: T, w: L + pw - xT, h: yT - T },
    { key: "spare", x: L, y: T, w: xT - L, h: yT - T },
    { key: "stretched", x: xT, y: yT, w: L + pw - xT, h: T + ph - yT },
    { key: "check", x: L, y: yT, w: xT - L, h: T + ph - yT },
  ];

  return (
    <SectionCard
      title="Every person, in one picture"
      subtitle={answer}
      padding={16}
      actions={<>{onViewAll ? <button type="button" onClick={onViewAll} style={ghost(t)}>Open team view</button> : null}<InfoDot metric="people" /></>}
    >
      <div ref={ref}>
        {loading ? (
          // Same axes + scattered points shape as the real quadrant, plus a
          // matching legend row — not one flat block.
          <>
            <div style={{ position: "relative", width: "100%", aspectRatio: `${W} / ${Hh}` }}>
              <span style={{ position: "absolute", left: `${(L / W) * 100}%`, top: `${(T / Hh) * 100}%`, right: `${(R / W) * 100}%`, bottom: `${(B / Hh) * 100}%`, borderLeft: `1px solid ${t.c.border}`, borderBottom: `1px solid ${t.c.border}` }} />
              {QUADRANT_DOT_SKELETON.map((p, i) => (
                <span key={i} style={{
                  position: "absolute", left: `${p.x}%`, top: `${p.y}%`, width: 9, height: 9,
                  borderRadius: "50%", background: t.c.borderStrong, opacity: 0.5,
                }} />
              ))}
            </div>
            <div style={{ display: "flex", gap: t.s[5], marginTop: t.s[3] }}>
              {[0, 1, 2, 3].map((i) => <Shim key={i} w={80} h={12} r={999} />)}
            </div>
          </>
        ) : !q.points.length ? (
          <Empty t={t}>No tracked time to plot in this window.</Empty>
        ) : (
          <>
            <svg viewBox={`0 0 ${W} ${Hh}`} style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
              role="img" aria-label="People plotted by hours tracked and active share">
              {zoneBoxes.map((z) => {
                const meta = ZONES.find((x) => x.key === z.key);
                if (z.w <= 0 || z.h <= 0) return null;
                return (
                  <g key={z.key}>
                    <rect x={z.x} y={z.y} width={z.w} height={z.h} fill={tone[meta.tone]} opacity={0.055} {...anim("fade", 0.05)} />
                    <text x={z.x + 11} y={z.y + 19} fill={tone[meta.tone]} fontSize={10.5} fontWeight={700}
                      letterSpacing=".07em" {...anim("fade", 0.14)}>{meta.label.toUpperCase()}</text>
                  </g>
                );
              })}
              <line x1={L} x2={L + pw} y1={T + ph} y2={T + ph} stroke={t.c.borderStrong} strokeWidth={1} {...anim("fade", 0.06)} />
              <line x1={L} x2={L} y1={T} y2={T + ph} stroke={t.c.borderStrong} strokeWidth={1} {...anim("fade", 0.06)} />
              <line x1={xT} x2={xT} y1={T} y2={T + ph} stroke={t.c.border} strokeWidth={1} {...anim("fade", 0.1)} />
              <line x1={L} x2={L + pw} y1={yT} y2={yT} stroke={t.c.border} strokeWidth={1} {...anim("fade", 0.1)} />

              {[0, 0.25, 0.5, 0.75, 1].map((f) => {
                const h = Math.round(q.xMax * f);
                return <text key={f} x={X(h)} y={Hh - 26} textAnchor="middle" fill={t.c.textTertiary} fontSize={10.5}
                  {...anim("fade", 0.12)}>{h}h</text>;
              })}
              {[yMin, yMin + Math.round((100 - yMin) / 3), yMin + Math.round(((100 - yMin) * 2) / 3), 100].map((u) => (
                <text key={u} x={L - 10} y={Y(u) + 3.5} textAnchor="end" fill={t.c.textTertiary} fontSize={10.5}
                  {...anim("fade", 0.12)}>{u}%</text>
              ))}
              <text x={L + pw / 2} y={Hh - 6} textAnchor="middle" fill={t.c.textTertiary} fontSize={10.5}
                fontWeight={600} letterSpacing=".07em" {...anim("fade", 0.16)}>HOURS TRACKED</text>
              <text x={14} y={T + ph / 2} textAnchor="middle" fill={t.c.textTertiary} fontSize={10.5} fontWeight={600}
                letterSpacing=".07em" transform={`rotate(-90 14 ${T + ph / 2})`} {...anim("fade", 0.16)}>ACTIVE SHARE</text>

              {q.points.map((p) => {
                const cx = X(p.hours); const cy = Y(p.share);
                const odd = p.zone !== "performing";
                return (
                  <Tooltip key={p.userId} mouseEnterDelay={0.05}
                    title={`${p.name} · ${p.position || "Team member"} — ${p.hours}h tracked, ${p.share}% active`}>
                    <circle
                      cx={cx} cy={cy} r={6}
                      fill={odd ? t.series[3] : t.series[1]}
                      stroke={t.c.surface} strokeWidth={2} opacity={odd ? 1 : 0.85}
                      className="tgp-pop"
                      role={onMember ? "button" : undefined}
                      onClick={onMember ? () => onMember(p) : undefined}
                      style={{ cursor: onMember ? "pointer" : "help", ...animStyle(0.24 + (p.hours / (q.xMax || 1)) * 0.42) }}
                    />
                  </Tooltip>
                );
              })}
            </svg>

            <div style={{ display: "flex", gap: `${t.s[2]}px ${t.s[5]}px`, flexWrap: "wrap", marginTop: t.s[3],
              fontSize: 11.5, color: t.c.textSecondary }} {...anim("rise", 0.82)}>
              {ZONES.map((z) => (
                <span key={z.key} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 3, background: tone[z.tone] }} />
                  <b style={{ color: t.c.text, fontWeight: 600 }}>{q.counts[z.key]}</b> {z.label.toLowerCase()}
                </span>
              ))}
              {q.untracked ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 3, background: t.c.borderStrong }} />
                  {q.untracked} tracked nothing — not plotted
                </span>
              ) : null}
            </div>
            <p style={{ margin: `${t.s[3]}px 0 0`, fontSize: 11, color: t.c.textTertiary }}>
              Targets: {q.hoursTarget}h over this window and {q.activeTarget}% active.
            </p>
          </>
        )}
      </div>
    </SectionCard>
  );
}

/* ═══════════════════ 4. the working day dial ═══════════════════════════ */
export function DayDial({ overview, loading }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const shape = React.useMemo(() => buildDayShape(overview?.hourCurve), [overview]);

  const answer = !shape ? "No hourly activity in this window."
    : shape.second !== null
      ? `Two peaks, not one — ${hh(shape.peak)} and ${hh(shape.second)}${shape.dipHour !== null ? `, with ${hh(shape.dipHour)} the quiet hour between them` : ""}.`
      : `The day peaks at ${hh(shape.peak)}. ${shape.corePct}% of active time sits inside 09:00–18:00.`;

  const CX = 160, CY = 160, R0 = 98, RMAX = 150, SW = 12;

  return (
    <SectionCard title="The shape of a working day" subtitle={answer} padding={16} actions={<InfoDot metric="dayShape" />}>
      <div ref={ref}>
        {loading ? (
          // Same ring + beat-list shape as the real dial, not one block.
          <>
            <div style={{ width: 220, height: 220, margin: "0 auto", position: "relative" }}>
              <Shim w={220} h={220} r={999} />
              <div style={{
                position: "absolute", inset: 34, borderRadius: "50%", background: t.c.surface,
              }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[3], marginTop: t.s[4] }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: "flex", gap: 11, alignItems: "center" }}>
                  <Shim w={24} h={24} r={7} />
                  <div style={{ flex: 1 }}>
                    <Shim w={`${60 - i * 8}%`} h={12} />
                    <Shim w={`${80 - i * 6}%`} h={10} style={{ marginTop: 6 }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : !shape ? (
          <Empty t={t}>No hourly activity recorded in this window.</Empty>
        ) : (
          <>
            <div style={{ position: "relative", width: "100%", maxWidth: 320, margin: "0 auto" }}>
              <svg viewBox="0 0 320 320" style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
                role="img" aria-label="24-hour radial chart of active time">
                <circle cx={CX} cy={CY} r={RMAX} fill="none" stroke={t.c.border} strokeWidth={1} {...anim("fade", 0.05)} />
                <circle cx={CX} cy={CY} r={R0} fill="none" stroke={t.c.borderStrong} strokeWidth={1} {...anim("fade", 0.05)} />
                {shape.curve.map((val, i) => {
                  const ang = (i * Math.PI * 2) / 24;
                  const sin = Math.sin(ang), cos = Math.cos(ang);
                  const len = (val / shape.max) * (RMAX - R0 - SW);
                  const a = [CX + sin * (R0 + SW / 2), CY - cos * (R0 + SW / 2)];
                  const b = [CX + sin * (R0 + SW / 2 + len), CY - cos * (R0 + SW / 2 + len)];
                  const isPeak = i === shape.peak || i === shape.second;
                  return (
                    <Tooltip key={i} title={`${hh(i)} · ${hm(val)} active`} mouseEnterDelay={0.05}>
                      <g>
                        {val > 0 ? (
                          <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                            stroke={isPeak ? t.c.primary : t.series[0]} strokeWidth={SW} strokeLinecap="round"
                            opacity={isPeak ? 1 : 0.74} className="tgp-draw" ref={drawRef(0.12 + i * 0.018)} />
                        ) : null}
                        <line
                          x1={CX + sin * (R0 - 6)} y1={CY - cos * (R0 - 6)}
                          x2={CX + sin * (RMAX + 12)} y2={CY - cos * (RMAX + 12)}
                          stroke="transparent" strokeWidth={26} style={{ cursor: "help" }}
                        />
                      </g>
                    </Tooltip>
                  );
                })}
                {[0, 6, 12, 18].map((h) => {
                  const ang = (h * Math.PI * 2) / 24;
                  return (
                    <text key={h} x={CX + Math.sin(ang) * (RMAX + 17)} y={CY - Math.cos(ang) * (RMAX + 17) + 4}
                      textAnchor="middle" fill={t.c.textTertiary} fontSize={10.5} fontWeight={600} letterSpacing=".06em"
                      {...anim("fade", 0.2)}>{String(h).padStart(2, "0")}</text>
                  );
                })}
                {[shape.peak, shape.second].filter((x) => x !== null).map((h, n) => {
                  const ang = (h * Math.PI * 2) / 24;
                  const r = R0 + SW / 2 + (shape.curve[h] / shape.max) * (RMAX - R0 - SW) + 14;
                  const px = CX + Math.sin(ang) * r, py = CY - Math.cos(ang) * r;
                  return (
                    <g key={h}>
                      <circle cx={px} cy={py} r={8.5} fill={t.c.primary} {...anim("pop", 0.82)} />
                      <text x={px} y={py + 3.4} textAnchor="middle" fontSize={9.5} fontWeight={700}
                        fill={inkOn(t.c.primary)} {...anim("fade", 0.9)}>{n + 1}</text>
                    </g>
                  );
                })}
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", pointerEvents: "none", textAlign: "center" }}>
                <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: t.c.textTertiary }}>Active</span>
                <span style={{ fontSize: 40, fontWeight: 600, letterSpacing: "-.035em", lineHeight: 1, marginTop: 6, color: t.c.text }}>
                  <CountUp value={H(shape.totalActive)} /><span style={{ fontSize: 16, fontWeight: 500, color: t.c.textSecondary }}>h</span>
                </span>
                <span style={{ fontSize: 11, color: t.c.textTertiary, marginTop: 6 }}>across 24 hours</span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[3], marginTop: t.s[4] }}>
              <Beat t={t} pin="1" delay={0.9} title={`${hh(shape.peak)} is the busiest hour`}
                body={`${H(shape.curve[shape.peak])}h of active time — ${pctOf(shape.curve[shape.peak], shape.totalActive)}% of the whole window.`} />
              {shape.second !== null ? (
                <Beat t={t} pin="2" delay={0.98} title={`A second peak at ${hh(shape.second)}`}
                  body={`Within ${Math.abs(Math.round(((shape.curve[shape.second] - shape.curve[shape.peak]) / shape.curve[shape.peak]) * 100))}% of the first. Protect both blocks from meetings.`} />
              ) : null}
              <Beat t={t} pin="•" muted delay={1.04} title={`${shape.eveningPct}% of work lands after 18:00`}
                body={shape.eveningPct >= 15 ? "High enough to be worth asking about." : "Low — evenings are largely clear."} />
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

const hh = (h) => `${String(h).padStart(2, "0")}:00`;

function Beat({ t, pin, title, body, muted, delay }) {
  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start", ...animStyle(delay) }} className="tgp-rise">
      <span style={{
        flex: "0 0 auto", width: 24, height: 24, borderRadius: 7, display: "grid", placeItems: "center",
        fontSize: 10, fontWeight: 700,
        background: muted ? t.c.surfaceMuted : t.c.primarySoft,
        color: muted ? t.c.textTertiary : t.c.primary,
        boxShadow: `inset 0 0 0 1px ${muted ? t.c.border : t.c.primaryRing}`,
      }}>{pin}</span>
      <span>
        <b style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: t.c.text }}>{title}</b>
        <span style={{ display: "block", fontSize: 11.5, color: t.c.textTertiary, marginTop: 2, lineHeight: 1.45 }}>{body}</span>
      </span>
    </div>
  );
}

/* ═══════════════════ 5. day by day ═════════════════════════════════════ */
export function RhythmCard({ overview, loading }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const series = overview?.daySeries || [];

  const up = series.filter((d) => d.prevTotalMinutes > 0 && d.activeMinutes + d.idleMinutes > d.prevTotalMinutes).length;
  const withPrev = series.filter((d) => d.prevTotalMinutes > 0).length;
  const peak = series.reduce((a, d) => (d.activeMinutes + d.idleMinutes > (a ? a.activeMinutes + a.idleMinutes : -1) ? d : a), null);
  const answer = !series.length ? "No daily data in this window."
    : `${withPrev ? `${up} of ${withPrev} days beat the previous period. ` : ""}${peak ? `${peak.label} was the peak at ${H(peak.activeMinutes + peak.idleMinutes)}h.` : ""}`;

  const W = 760, Hh = 290, L = 50, R = 18, T = 20, B = 60;
  const pw = W - L - R, ph = Hh - T - B;
  const rawMax = Math.max(1, ...series.map((d) => Math.max(d.activeMinutes + d.idleMinutes, d.prevTotalMinutes || 0)));
  const stepMin = niceStep(rawMax);
  const max = Math.ceil(rawMax / stepMin) * stepMin;
  const Y = (m) => T + ph - (m / max) * ph;
  const band = series.length ? pw / series.length : pw;
  const bw = Math.min(38, band * 0.4);
  const ticks = [0, max / 3, (max * 2) / 3, max];
  const pts = series.map((d, i) => [L + band * i + band / 2, Y(d.prevTotalMinutes || 0)]);
  const hasPrev = series.some((d) => d.prevTotalMinutes > 0);

  return (
    <SectionCard
      title="Day by day"
      subtitle={answer}
      padding={16}
      actions={
        <span style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: t.c.textSecondary }}>
          <Key t={t} color={t.series[0]}>Active</Key>
          <Key t={t} color={t.idle}>Idle</Key>
          {hasPrev ? <Key t={t} color={t.c.textSecondary} rule>Last period</Key> : null}
          <InfoDot metric="rhythm" />
        </span>
      }
    >
      <div ref={ref}>
        {loading ? (
          // Same bar-column shape as the real chart, not one flat block.
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 240, padding: "0 4px" }}>
            {RHYTHM_BAR_SKELETON.map((hPct, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ width: "100%", maxWidth: 38, height: 180, display: "flex", alignItems: "flex-end" }}>
                  <Shim w="100%" h={Math.round(180 * hPct)} r={5} />
                </span>
                <Shim w={26} h={11} />
              </div>
            ))}
          </div>
        ) : !series.length ? (
          <Empty t={t}>No daily activity in this window.</Empty>
        ) : (
          <svg viewBox={`0 0 ${W} ${Hh}`} style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
            role="img" aria-label="Daily active and idle hours">
            {ticks.map((m, i) => (
              <g key={i}>
                <line x1={L} x2={W - R} y1={Y(m)} y2={Y(m)} stroke={i === 0 ? t.c.borderStrong : t.c.border} strokeWidth={1} {...anim("fade", 0.06)} />
                <text x={L - 11} y={Y(m) + 3.5} textAnchor="end" fill={t.c.textTertiary} fontSize={10.5} {...anim("fade", 0.1)}>{H(m)}h</text>
              </g>
            ))}
            {series.map((d, i) => {
              const cx = L + band * i + band / 2; const x = cx - bw / 2;
              const total = d.activeMinutes + d.idleMinutes;
              const idleH = Math.max(0, Y(d.activeMinutes) - Y(total) - 2);
              return (
                <Tooltip key={d.dateLocal || i} mouseEnterDelay={0.05}
                  title={`${d.label} · ${H(d.activeMinutes)}h active, ${H(d.idleMinutes)}h idle${d.prevTotalMinutes ? ` — last period ${H(d.prevTotalMinutes)}h` : ""}`}>
                  <g style={{ cursor: "help" }}>
                    <rect x={L + band * i} y={T} width={band} height={ph + 46} fill="transparent" />
                    <g className="tgp-gy" style={animStyle(0.14 + i * 0.06)}>
                      <rect x={x} y={Y(d.activeMinutes)} width={bw} height={Math.max(0, Y(0) - Y(d.activeMinutes))} fill={t.series[0]} />
                      {idleH > 0 ? <path d={roundTop(x, Y(total), bw, idleH, 5)} fill={t.idle} /> : null}
                    </g>
                  </g>
                </Tooltip>
              );
            })}
            {hasPrev ? (
              <>
                <path d={pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("")}
                  fill="none" stroke={t.c.textSecondary} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                  className="tgp-draw" ref={drawRef(0.5)} />
                {pts.map((p, i) => (
                  <circle key={i} cx={p[0]} cy={p[1]} r={4} fill={t.c.textSecondary} stroke={t.c.surface} strokeWidth={2}
                    {...anim("pop", 0.74 + i * 0.04)} />
                ))}
              </>
            ) : null}
            {series.map((d, i) => {
              const cx = L + band * i + band / 2;
              const total = d.activeMinutes + d.idleMinutes;
              const delta = d.prevTotalMinutes > 0 ? Math.round(((total - d.prevTotalMinutes) / d.prevTotalMinutes) * 100) : null;
              return (
                <g key={`lab${i}`}>
                  {peak && d === peak ? (
                    <text x={cx} y={Y(total) - 13} textAnchor="middle" fill={t.c.text} fontSize={12} fontWeight={600}
                      {...anim("fade", 0.58)}>{H(total)}h</text>
                  ) : null}
                  <text x={cx} y={Hh - 40} textAnchor="middle" fill={t.c.textSecondary} fontSize={11.5} fontWeight={600}
                    {...anim("fade", 0.18 + i * 0.06)}>{d.label}</text>
                  {delta !== null ? (
                    <text x={cx} y={Hh - 21} textAnchor="middle" fontSize={11} fontWeight={600}
                      fill={delta === 0 ? t.c.textTertiary : delta > 0 ? t.status.positive : t.status.negative}
                      {...anim("rise", 0.82 + i * 0.045)}>
                      {delta === 0 ? "level" : `${delta > 0 ? "▲" : "▼"} ${Math.abs(delta)}%`}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        )}
      </div>
    </SectionCard>
  );
}

function Key({ t, color, rule, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <i style={{ width: rule ? 15 : 9, height: rule ? 2 : 9, borderRadius: rule ? 2 : 3, background: color, flex: "0 0 auto" }} />
      {children}
    </span>
  );
}

const roundTop = (x, y, w, h, r0) => {
  const r = Math.min(r0, h, w / 2);
  return `M${x} ${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h}H${x}Z`;
};
const niceStep = (max) => {
  const raw = max / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10;
};

/* ═══════════════════ 6. week × hour heatmap ════════════════════════════ */
export function WeekHeatmap({ overview, loading }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const heat = React.useMemo(() => buildHeat(overview?.weekMatrix, t.heat.length), [overview, t.heat.length]);
  const shape = React.useMemo(() => buildDayShape(overview?.hourCurve), [overview]);
  const win = React.useMemo(() => activeHourWindow(overview?.hourCurve), [overview]);
  const [hover, setHover] = React.useState(null);

  const cols = win.to - win.from + 1;
  const W = 760, L = 46, RW = 104;
  const gridR = W - RW, cw = (gridR - L) / Math.max(cols, 1);
  const stripB = 74, stripH = 44, gridT = 90, ch = 24;
  const rows = heat ? heat.matrix.length : 0;
  const gridB = gridT + ch * rows;
  const X = (h) => L + cw * (h - win.from);
  const maxHour = shape ? shape.max : 1;

  const answer = !heat ? "No hourly detail in this window."
    : `${shape ? `${shape.corePct}% of active time sits inside 09:00–18:00. ` : ""}`
      + `Busiest single hour: ${WEEKDAYS[heat.peak.day]} ${hh(heat.peak.hour)} at ${H(heat.peak.minutes)}h.`;

  return (
    <SectionCard
      title="When your company is actually online"
      subtitle={answer}
      padding={16}
      actions={
        <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: t.c.textTertiary, whiteSpace: "nowrap" }}>
          <span>0h</span>
          <span style={{ display: "flex", gap: 2 }}>
            {t.heat.map((c) => <i key={c} style={{ width: 15, height: 9, borderRadius: 2, background: c }} />)}
          </span>
          <span>{heat ? `${H(heat.max)}h in one hour` : ""}</span>
          <InfoDot metric="matrix" />
        </span>
      }
    >
      <div ref={ref}>
        {loading ? (
          // Same hour-strip + 7-row grid shape as the real heatmap, not one block.
          <>
            <div style={{ display: "flex", gap: 3, height: 44, alignItems: "flex-end", marginLeft: 46 }}>
              {HEATMAP_STRIP_SKELETON.map((hPct, i) => (
                <Shim key={i} w="100%" h={Math.round(44 * hPct)} r={3} style={{ flex: 1 }} />
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 16 }}>
              {WEEKDAYS.map((wd) => (
                <div key={wd} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <span style={{ width: 40, fontSize: 10.5, color: t.c.textTertiary, textAlign: "right", paddingRight: 6, flexShrink: 0 }}>{wd}</span>
                  {HEATMAP_STRIP_SKELETON.map((op, i) => (
                    <Shim key={i} h={24} r={4} style={{ flex: 1, opacity: 0.35 + op * 0.5 }} />
                  ))}
                  <Shim w={30} h={11} style={{ marginLeft: 8, flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </>
        ) : !heat ? (
          <Empty t={t}>No hour-level activity in this window.</Empty>
        ) : (
          <>
            <svg viewBox={`0 0 ${W} ${gridB + 58}`} style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
              role="img" aria-label="Heatmap of active time by day and hour"
              onMouseLeave={() => setHover(null)}>
              <text x={L} y={20} fill={t.c.textTertiary} fontSize={9.5} fontWeight={700} letterSpacing=".09em" {...anim("fade", 0)}>BY HOUR</text>
              <line x1={L} x2={gridR} y1={stripB} y2={stripB} stroke={t.c.borderStrong} strokeWidth={1} {...anim("fade", 0.05)} />
              {range(win.from, win.to).map((h) => {
                const val = shape ? shape.curve[h] : 0;
                const bh = val > 0 ? Math.max(2, (val / maxHour) * stripH) : 0;
                const isPeak = shape && (h === shape.peak || h === shape.second);
                const isDip = shape && h === shape.dipHour;
                const dim = hover && hover.hour !== h;
                return (
                  <Tooltip key={`s${h}`} title={`${hh(h)} · ${H(val)}h across the whole window`} mouseEnterDelay={0.05}>
                    <g style={{ cursor: "help" }} onMouseEnter={() => setHover({ hour: h, day: null })}>
                      <rect x={X(h)} y={stripB - stripH - 14} width={cw} height={stripH + 14} fill="transparent" />
                      {bh > 0 ? (
                        <path d={roundTop(X(h) + 3, stripB - bh, cw - 6, bh, 3)}
                          fill={isPeak ? t.c.primary : isDip ? t.series[6] : t.series[0]}
                          opacity={dim ? 0.18 : (isPeak || isDip ? 1 : 0.45)}
                          className="tgp-gy tgp-x" style={animStyle((h - win.from) * 0.023)} />
                      ) : null}
                    </g>
                  </Tooltip>
                );
              })}

              {heat.matrix.map((row, d) => {
                const weekend = d >= 5;
                const dim = hover && hover.day !== null && hover.day !== d;
                return (
                  <g key={d}>
                    <text x={L - 10} y={gridT + ch * d + ch / 2 + 3.5} textAnchor="end" fontSize={10.5} fontWeight={600}
                      className="tgp-fade tgp-lab" style={animStyle(d * 0.008)}
                      fill={hover && hover.day === d ? t.c.primary : weekend ? t.c.textTertiary : t.c.textSecondary}>
                      {WEEKDAYS[d]}
                    </text>
                    {range(win.from, win.to).map((h) => {
                      const val = row[h] || 0;
                      const off = hover && hover.hour !== h && hover.day !== d;
                      return (
                        <Tooltip key={h} title={`${WEEKDAYS[d]} ${hh(h)} · ${val > 0 ? hm(val) : "nothing"} active`} mouseEnterDelay={0.05}>
                          <g onMouseEnter={() => setHover({ hour: h, day: d })} style={{ cursor: "pointer" }}>
                            <rect x={X(h) + 1.5} y={gridT + ch * d + 1.5} width={cw - 3} height={ch - 3} rx={4}
                              fill={t.heat[heat.bucket(val)]} opacity={off ? 0.3 : 1}
                              className="tgp-pop tgp-x" style={animStyle((h - win.from) * 0.023 + d * 0.008)} />
                            <rect x={X(h)} y={gridT + ch * d} width={cw} height={ch} fill="transparent" />
                          </g>
                        </Tooltip>
                      );
                    })}
                    <g className="tgp-gx" style={animStyle((win.to - win.from) * 0.023 + 0.06 + d * 0.032)}>
                      <rect x={gridR + 12} y={gridT + ch * d + ch / 2 - 4}
                        width={Math.max(3, (heat.dayTotals[d] / heat.maxDay) * 44)} height={8} rx={4}
                        fill={t.series[0]} opacity={dim ? 0.25 : weekend ? 0.4 : 0.8} />
                    </g>
                    <text x={W} y={gridT + ch * d + ch / 2 + 3.5} textAnchor="end" fontSize={10.5} fontWeight={600}
                      fill={weekend ? t.c.textTertiary : t.c.textSecondary}
                      {...anim("fade", (win.to - win.from) * 0.023 + 0.1 + d * 0.032)}>{H(heat.dayTotals[d])}h</text>
                  </g>
                );
              })}
              <text x={gridR + 12} y={gridT - 11} fill={t.c.textTertiary} fontSize={9.5} fontWeight={700} letterSpacing=".09em"
                {...anim("fade", (win.to - win.from) * 0.023 + 0.06)}>BY DAY</text>

              {range(win.from, win.to).filter((h) => (h - win.from) % 2 === 0).map((h) => (
                <text key={`x${h}`} x={X(h) + cw / 2} y={gridB + 19} textAnchor="middle" fontSize={10.5}
                  className="tgp-fade tgp-lab" style={animStyle((h - win.from) * 0.023)}
                  fill={hover && hover.hour === h ? t.c.primary : t.c.textTertiary}>{String(h).padStart(2, "0")}</text>
              ))}
              {win.from <= 9 && win.to >= 18 ? (
                <g {...anim("fade", (win.to - win.from) * 0.023 + 0.29)}>
                  <path d={`M${X(9)} ${gridB + 38}V${gridB + 33}H${X(18)}V${gridB + 38}`} fill="none"
                    stroke={t.c.primary} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  <text x={(X(9) + X(18)) / 2} y={gridB + 52} textAnchor="middle" fill={t.c.primary} fontSize={10.5}
                    fontWeight={700} letterSpacing=".05em">
                    CORE HOURS 09:00–18:00{shape ? ` · ${shape.corePct}% OF ALL ACTIVE TIME` : ""}
                  </text>
                </g>
              ) : null}
            </svg>
            <p style={{ margin: `${t.s[3]}px 0 0`, fontSize: 10.5, color: t.c.textTertiary }}>
              {String(win.from).padStart(2, "0")}:00–{String(Math.min(win.to + 1, 24)).padStart(2, "0")}:00 shown
              {win.hiddenPct > 0 ? ` — ${win.hiddenPct}% of active time falls outside that window.` : " — nothing was tracked outside it."}
            </p>
          </>
        )}
      </div>
    </SectionCard>
  );
}

const range = (a, b) => Array.from({ length: Math.max(0, b - a + 1) }, (_, i) => a + i);

/* ═══════════════════ 7. time split + what we can see ═══════════════════ */
export function SplitCard({ overview, loading }) {
  const t = useDashTheme();
  const ref = useRevealRoot([overview]);
  const tot = overview?.totals;
  const cov = overview?.detail?.coverage;
  const sw = overview?.detail?.switches;

  if (loading) {
    // Same split-bar + stat-row shape as the real card, not one flat block.
    return (
      <SectionCard title="Where the time went" padding={16} actions={<InfoDot metric="tools" />}>
        <Shim w={110} h={11} />
        <div style={{ display: "flex", gap: 2, height: 34, marginTop: 9 }}>
          <Shim w="68%" h="100%" r={8} />
          <Shim w="32%" h="100%" r={8} />
        </div>
        <Shim w={140} h={11} style={{ marginTop: 24 }} />
        <Shim w={90} h={30} style={{ marginTop: 10 }} />
        <Shim w={150} h={11} style={{ marginTop: 24 }} />
        <Shim h={9} r={999} style={{ marginTop: 9 }} />
      </SectionCard>
    );
  }
  if (!tot || !tot.trackedMinutes) {
    return <SectionCard title="Where the time went" padding={16}><Empty t={t}>Nothing tracked in this window.</Empty></SectionCard>;
  }

  const activePct = pctOf(tot.activeMinutes, tot.trackedMinutes);
  const idlePct = 100 - activePct;
  const covPct = cov && cov.total > 0 ? pctOf(cov.covered, cov.total) : null;
  const swDelta = sw && sw.prevTotal > 0 ? Math.round(((sw.total - sw.prevTotal) / sw.prevTotal) * 100) : null;

  return (
    <SectionCard
      title="Where the time went"
      subtitle={`${H(tot.idleMinutes)}h of ${H(tot.trackedMinutes)}h was idle — ${idlePct}%. ${idlePct >= 25 ? "High enough to be worth a look." : "Within a healthy band."}`}
      padding={16}
    >
      <div ref={ref}>
        <Micro t={t}>Active vs idle</Micro>
        <div style={{ display: "flex", gap: 2, height: 34, marginTop: 9 }}>
          <div className="tgp-gx" style={{
            flex: `${activePct} 1 0`, background: t.series[0], color: inkOn(t.series[0]),
            borderRadius: "8px 3px 3px 8px", display: "grid", placeItems: "center",
            fontSize: 11.5, fontWeight: 700, ...animStyle(0.12),
          }}>{activePct}%</div>
          <div className="tgp-gx" style={{
            flex: `${Math.max(idlePct, 1)} 1 0`, background: t.idle, color: inkOn(t.idle),
            borderRadius: "3px 8px 8px 3px", display: "grid", placeItems: "center",
            fontSize: 11.5, fontWeight: 700, ...animStyle(0.21),
          }}>{idlePct}%</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 9, fontSize: 11.5 }} {...anim("fade", 0.34)}>
          <span style={{ color: t.c.textSecondary }}><b style={{ color: t.c.text }}>{H(tot.activeMinutes)}h</b> active</span>
          <span style={{ color: t.c.textTertiary }}><b style={{ color: t.c.textSecondary }}>{H(tot.idleMinutes)}h</b> idle</span>
        </div>

        {sw ? (
          <>
            <Micro t={t} style={{ marginTop: 24 }}>Tool switching</Micro>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 8 }} {...anim("rise", 0.42)}>
              <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-.025em", lineHeight: 1, color: t.c.text }}>
                <CountUp value={sw.total} />
              </span>
              {swDelta !== null ? (
                <span style={{ fontSize: 11, fontWeight: 600, color: swDelta > 0 ? t.status.negative : t.status.positive }}>
                  {swDelta > 0 ? "▲" : "▼"} {Math.abs(swDelta)}% vs last period
                </span>
              ) : null}
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.55 }} {...anim("fade", 0.5)}>
              {sw.avgGapMinutes ? `About ${sw.avgGapMinutes} minutes between switches. ` : ""}
              A rising count usually means more interruption, not more work.
            </p>
          </>
        ) : null}

        {covPct !== null ? (
          <>
            <Micro t={t} style={{ marginTop: 24 }}>How much we can see</Micro>
            <div style={{ height: 9, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden",
              boxShadow: `inset 0 0 0 1px ${t.c.border}`, marginTop: 9 }}>
              <i className="tgp-gx" style={{ display: "block", height: "100%", width: `${covPct}%`,
                borderRadius: 999, background: t.series[1], ...animStyle(0.58) }} />
            </div>
            <p style={{ margin: "9px 0 0", fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.55 }} {...anim("fade", 0.64)}>
              <b style={{ color: t.c.text }}>{cov.covered} of {cov.total} people</b> have app-level detail. Tool switching and
              the hour curve are computed from those {cov.covered}; every total above uses all {cov.total}.
            </p>
          </>
        ) : null}
      </div>
    </SectionCard>
  );
}

/* ── shared bits ──────────────────────────────────────────────────────── */
function Micro({ t, children, style }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase",
      color: t.c.textTertiary, ...style }}>{children}</div>
  );
}
function Empty({ t, children }) {
  return (
    <p style={{ margin: 0, padding: `${t.s[6]}px 0`, textAlign: "center", fontSize: 12.5, color: t.c.textTertiary }}>
      {children}
    </p>
  );
}
const ghost = (t) => ({
  appearance: "none", background: "transparent", border: `1px solid ${t.c.border}`, color: t.c.textSecondary,
  font: "inherit", fontSize: 12, fontWeight: 500, padding: "5px 10px", borderRadius: 8, cursor: "pointer",
});
